import { memo, useMemo } from "react";
import Plot from "react-plotly.js";
import type { IrrDataPoint } from "./types";
import { getDataForDate, getIrrValue } from "./utils";

/** Memoized bar chart Plot - expensive, should not re-render on excludeInput keystroke. */
export const IRRBarChart = memo(function IRRBarChart({
  activeDate,
  allExcludedRoots,
  irrData,
  roots,
  sofrAdjusted,
  dates,
  activeDateIndex,
  onSliderEnd,
}: {
  activeDate: string;
  allExcludedRoots: string[];
  irrData: IrrDataPoint[];
  roots: string[];
  sofrAdjusted: boolean;
  dates: string[];
  activeDateIndex: number;
  onSliderEnd: (event: unknown) => void;
}) {
  const filteredData = irrData.filter((d) => !allExcludedRoots.includes(d.root));
  const allY = filteredData.flatMap((d) => d.data.map((p) => getIrrValue(p, sofrAdjusted)));
  const maxY = allY.length > 0 ? Math.max(...allY) : 0.1;
  const minY = allY.length > 0 ? Math.min(...allY) : 0;
  const paddedMax = Math.max(maxY * 1.1, maxY + 0.0175);
  const paddedMin = Math.min(minY * 1.5, minY - 0.0175, 0);

  const data = useMemo(
    () => getDataForDate(activeDate, allExcludedRoots, irrData, roots, sofrAdjusted),
    [activeDate, allExcludedRoots, irrData, roots, sofrAdjusted]
  );
  const layout = useMemo(
    () => ({
      autosize: true,
      title: "IRR Analysis",
      uirevision: "irr-bar-chart",
      dragmode: "zoom" as const,
      xaxis: { title: "Root", fixedrange: true },
      yaxis: { title: "IRR", fixedrange: true, range: [paddedMin, paddedMax] as [number, number] },
      sliders: [
        {
          active: activeDateIndex,
          steps: dates.map((date) => {
            const d = new Date(date + "T00:00:00");
            const formatted =
              d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
              " '" +
              String(d.getFullYear()).slice(-2);
            return {
              args: [
                [date],
                { frame: { duration: 0, redraw: true }, mode: "immediate" as const, transition: { duration: 0 } },
              ],
              label: formatted,
              value: formatted,
              method: "animate" as const,
            };
          }),
          pad: { t: 50 },
          x: 0.1,
          len: 0.8,
          xanchor: "auto" as const,
          y: 0,
          yanchor: "top" as const,
          currentvalue: { visible: true, prefix: "Date: ", xanchor: "center" as const },
        },
      ],
      margin: { b: 80, t: 50, l: 50, r: 30 },
    }),
    [activeDateIndex, dates, paddedMin, paddedMax]
  );
  const frames = useMemo(
    () =>
      dates.map((date) => ({
        name: date,
        data: getDataForDate(date, allExcludedRoots, irrData, roots, sofrAdjusted),
      })) as React.ComponentProps<typeof Plot>["frames"],
    [dates, allExcludedRoots, irrData, roots, sofrAdjusted]
  );

  // Key forces remount when sofrAdjusted toggles. Plotly resets slider when frames/data change,
  // so we remount with the correct activeDateIndex from React state to preserve position.
  return (
    <Plot
      key={sofrAdjusted ? "sofr" : "raw"}
      className="w-100 h-100"
      style={{ width: "100%", height: "100%" }}
      useResizeHandler
      data={data}
      layout={layout}
      frames={frames}
      onSliderEnd={onSliderEnd}
      config={{ displaylogo: false, responsive: true }}
    />
  );
});
